import { useState, useEffect } from 'react';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

let cachedSpecialty: string | null | undefined;
let cachePromise: Promise<string | null> | null = null;

/**
 * Consumidores ativos do hook. Guardados para que uma invalidação (troca de
 * unidade ou salvamento das configurações) atualize menu e abas sem exigir F5,
 * e com um único request em vez de um por componente montado.
 */
const subscribers = new Set<(specialty: string | null) => void>();

async function fetchSpecialty(): Promise<string | null> {
  if (cachedSpecialty !== undefined) return cachedSpecialty;
  if (!cachePromise) {
    cachePromise = axios
      .get(getApiUrl('/clinic/settings'))
      .then((res) => {
        const v = res.data?.data?.clinic_medical_specialty;
        cachedSpecialty = typeof v === 'string' && v.trim() ? v.trim().toLowerCase() : null;
        return cachedSpecialty;
      })
      .catch(() => {
        cachedSpecialty = null;
        return null;
      });
  }
  return cachePromise;
}

export function invalidateClinicMedicalSpecialtyCache(): void {
  cachedSpecialty = undefined;
  cachePromise = null;
  if (subscribers.size) {
    void fetchSpecialty().then((specialty) => {
      subscribers.forEach((notify) => notify(specialty));
    });
  }
}

// Cada unidade tem sua própria especialidade, então trocar de unidade precisa
// reavaliar o gating. Registrado no módulo para não duplicar por componente.
if (typeof window !== 'undefined') {
  window.addEventListener('unit:changed', () => invalidateClinicMedicalSpecialtyCache());
}

export function useClinicMedicalSpecialty(): {
  specialty: string | null;
  isOdontologia: boolean;
  isFisioterapia: boolean;
  loading: boolean;
} {
  const [specialty, setSpecialty] = useState<string | null>(cachedSpecialty ?? null);
  const [loading, setLoading] = useState(cachedSpecialty === undefined);

  useEffect(() => {
    let cancelled = false;

    const apply = (value: string | null) => {
      if (cancelled) return;
      setSpecialty(value);
      setLoading(false);
    };

    subscribers.add(apply);
    void fetchSpecialty().then(apply);

    return () => {
      cancelled = true;
      subscribers.delete(apply);
    };
  }, []);

  return {
    specialty,
    isOdontologia: specialty === 'odontologia',
    isFisioterapia: specialty === 'fisioterapia',
    loading,
  };
}
